<app-header sectionName="Reports"></app-header>

<div class="page">
  <nav mat-tab-nav-bar [tabPanel]="panel" class="tabs">
    <a mat-tab-link [active]="tab === 'payables'" (click)="selectTab('payables')">
      <mat-icon class="tab-icon">swap_horiz</mat-icon> Transfers &amp; buy contracts
    </a>
    <a mat-tab-link [active]="tab === 'sell'" (click)="selectTab('sell')">
      <mat-icon class="tab-icon">sell</mat-icon> Sell contracts
    </a>
    <a mat-tab-link [active]="tab === 'suppliers'" (click)="selectTab('suppliers')">
      <mat-icon class="tab-icon">campaign</mat-icon> Suppliers
    </a>
  </nav>

  <mat-tab-nav-panel #panel>
  <section class="list-toolbar">
    @if (tab === 'payables') {
    <app-supplier-filter class="f-supplier"
      [supplierId]="supplierId"
      (supplierIdChange)="onSupplierChange($event)"></app-supplier-filter>
    }

    <mat-form-field appearance="outline" class="f-date" subscriptSizing="dynamic">
      <mat-label>From</mat-label>
      <input matInput type="date" [(ngModel)]="fromDate" (change)="filtersChanged()" [attr.max]="toDate || null">
    </mat-form-field>

    <mat-form-field appearance="outline" class="f-date" subscriptSizing="dynamic">
      <mat-label>To</mat-label>
      <input matInput type="date" [(ngModel)]="toDate" (change)="filtersChanged()" [attr.min]="fromDate || null">
    </mat-form-field>

    @if (hasFilters) {
    <button mat-button type="button" (click)="clearFilters()">
      <mat-icon>filter_alt_off</mat-icon> Clear filters
    </button>
    }
    <span class="spacer"></span>
    <button mat-icon-button type="button" (click)="refresh()" [disabled]="loading" matTooltip="Refresh">
      <mat-icon>refresh</mat-icon>
    </button>
  </section>

  @if (loading) {
  <div class="table-state"><mat-spinner diameter="28"></mat-spinner></div>
  } @else if (error) {
  <div class="table-state error">{{ error }}</div>
  } @else if (tab === 'payables') {

  @if (!payables || payables.rows.length === 0) {
  <div class="table-state">Nothing payable in this range.</div>
  } @else {
  <!-- Range totals: every day matching the filters, not only this page. -->
  <div class="tiles">
    @for (t of [
      { label: 'Total', icon: 'account_balance_wallet', v: payables.totals.total },
      { label: 'Transfer runs', icon: 'swap_horiz', v: payables.totals.runs },
      { label: 'Buy contracts', icon: 'description', v: payables.totals.contracts }
    ]; track t.label) {
    <div class="tile" [class.tile-total]="t.label === 'Total'">
      <div class="tile-head">
        <mat-icon>{{ t.icon }}</mat-icon> {{ t.label }}
        <span class="tile-meta">{{ t.v.count | number }} · {{ t.v.coins | number }} coins</span>
      </div>
      <div class="tile-amount num">{{ t.v.payable | number }}</div>
      <div class="tile-split">
        <span>Paid <b class="paid-amount">{{ t.v.paid | number }}</b></span>
        <span>Unpaid <b class="unpaid-amount">{{ t.v.unpaid | number }}</b></span>
      </div>
    </div>
    }
  </div>

  @if (payablesCharts; as ch) {
  <section class="charts" [class.stale]="chartsLoading">
    <div class="chart-card wide">
      <div class="chart-head">
        <h3>Owed per day</h3>
        <p>What suppliers earned each day, and how much of it is paid. Hover a day for runs vs buy contracts.</p>
      </div>
      <app-stack-chart [days]="ch.owed" [series]="paidUnpaid" totalLabel="Owed" ariaLabel="Owed per day, paid and unpaid" />
    </div>
    <div class="chart-card">
      <div class="chart-head">
        <h3>Unpaid, building up</h3>
        <p>Running total of what is still unpaid, by the day it was earned. The last point is what you owe for this range.</p>
      </div>
      <app-line-chart [dates]="ch.unpaidRunning.dates" [series]="ch.unpaidRunning.series" [area]="true" ariaLabel="Unpaid running total" />
    </div>
    <div class="chart-card">
      <div class="chart-head">
        <h3>Paid so far</h3>
        <p>Of {{ ch.owedTotal | number }} owed in the charted days.</p>
      </div>
      <app-share-bar [parts]="ch.paidMeter" [track]="ch.owedTotal" />
      <div class="chart-head second">
        <h3>Where it comes from</h3>
        <p>Amount owed by source.</p>
      </div>
      <app-share-bar [parts]="ch.sources" />
    </div>
  </section>
  @if (ch.truncated) { <p class="chart-note">Charts show the last {{ chartDays }} days of the range; the totals and table cover all of it.</p> }
  }

  <p class="tz" matTooltip="Transfer runs count on the day they confirmed, buy contracts on the day they finished. Buy contracts that received no coins are left out.">
    <mat-icon>schedule</mat-icon> Days are in <b>{{ timezone }}</b> · all {{ payables.total | number }} days in range totalled above.
  </p>

  @if (isMobile$ | async) {
  <div class="cards">
    @for (r of payables.rows; track r.date) {
    <div class="card">
      <div class="card-head">
        <div class="card-title">{{ r.date }}</div>
        <span class="card-meta">Payable {{ r.total.payable | number }}</span>
      </div>
      <div class="split-line">
        <span>Paid <b class="paid-amount">{{ r.total.paid | number }}</b></span>
        <span>Unpaid <b class="unpaid-amount">{{ r.total.unpaid | number }}</b></span>
      </div>
      @for (s of [{ label: 'Runs', v: r.runs }, { label: 'Buy contracts', v: r.contracts }]; track s.label) {
      @if (s.v.count) {
      <div class="source-line">
        <span class="source-label">{{ s.label }} · {{ s.v.count | number }} · {{ s.v.coins | number }} coins</span>
        <span class="num"><span class="paid-amount">{{ s.v.paid | number }}</span> / <span class="unpaid-amount">{{ s.v.unpaid | number }}</span></span>
      </div>
      }
      }
    </div>
    }
  </div>
  } @else {
  <div class="table-card table-scroll">
    <table class="report-table">
      <thead>
        <tr>
          <th rowspan="2">Day</th>
          <th colspan="3" class="grp">Total</th>
          <th colspan="4" class="grp">Transfer runs</th>
          <th colspan="4" class="grp">Buy contracts</th>
        </tr>
        <tr>
          <th class="num">Payable</th><th class="num">Paid</th><th class="num">Unpaid</th>
          <th class="num grp-start">Runs</th><th class="num">Coins</th><th class="num">Paid</th><th class="num">Unpaid</th>
          <th class="num grp-start">Contracts</th><th class="num">Coins</th><th class="num">Paid</th><th class="num">Unpaid</th>
        </tr>
      </thead>
      <tbody>
        @for (r of payables.rows; track r.date) {
        <tr>
          <td>{{ r.date }}</td>
          <td class="num strong">{{ r.total.payable | number }}</td>
          <td class="paid-amount">{{ r.total.paid | number }}</td>
          <td class="unpaid-amount">{{ r.total.unpaid | number }}</td>
          <td class="num grp-start">{{ r.runs.count | number }}</td>
          <td class="num">{{ r.runs.coins | number }}</td>
          <td class="paid-amount">{{ r.runs.paid | number }}</td>
          <td class="unpaid-amount">{{ r.runs.unpaid | number }}</td>
          <td class="num grp-start">{{ r.contracts.count | number }}</td>
          <td class="num">{{ r.contracts.coins | number }}</td>
          <td class="paid-amount">{{ r.contracts.paid | number }}</td>
          <td class="unpaid-amount">{{ r.contracts.unpaid | number }}</td>
        </tr>
        }
      </tbody>
      <tfoot>
        <tr>
          <td>All {{ payables.total | number }} days</td>
          <td class="num strong">{{ payables.totals.total.payable | number }}</td>
          <td class="paid-amount">{{ payables.totals.total.paid | number }}</td>
          <td class="unpaid-amount">{{ payables.totals.total.unpaid | number }}</td>
          <td class="num grp-start">{{ payables.totals.runs.count | number }}</td>
          <td class="num">{{ payables.totals.runs.coins | number }}</td>
          <td class="paid-amount">{{ payables.totals.runs.paid | number }}</td>
          <td class="unpaid-amount">{{ payables.totals.runs.unpaid | number }}</td>
          <td class="num grp-start">{{ payables.totals.contracts.count | number }}</td>
          <td class="num">{{ payables.totals.contracts.coins | number }}</td>
          <td class="paid-amount">{{ payables.totals.contracts.paid | number }}</td>
          <td class="unpaid-amount">{{ payables.totals.contracts.unpaid | number }}</td>
        </tr>
      </tfoot>
    </table>
  </div>
  }
  }

  } @else if (tab === 'sell') {

  @if (!sell || sell.rows.length === 0) {
  <div class="table-state">No finished sell contracts in this range.</div>
  } @else {
  <div class="tiles">
    <div class="tile tile-total">
      <div class="tile-head">
        <mat-icon>sell</mat-icon> Sell contracts
        <span class="tile-meta">{{ sell.totals.count | number }} contracts</span>
      </div>
      <div class="tile-amount num">{{ sell.totals.amount | number }}</div>
      <div class="tile-split">
        <span>Paid <b class="paid-amount">{{ sell.totals.paid | number }}</b></span>
        <span>Unpaid <b class="unpaid-amount">{{ sell.totals.unpaid | number }}</b></span>
      </div>
    </div>
    <div class="tile">
      <div class="tile-head"><mat-icon>toll</mat-icon> Coins</div>
      <div class="tile-split stacked">
        <span>Sent <b class="num">{{ sell.totals.sent_coins | number }}</b></span>
        <span>Received <b class="num">{{ sell.totals.received_coins | number }}</b></span>
      </div>
    </div>
  </div>

  @if (sellCharts; as ch) {
  <section class="charts" [class.stale]="chartsLoading">
    <div class="chart-card wide">
      <div class="chart-head">
        <h3>Billed per day</h3>
        <p>What finished sell contracts billed each day, and how much of it is paid.</p>
      </div>
      <app-stack-chart [days]="ch.billed" [series]="paidUnpaid" totalLabel="Billed" ariaLabel="Billed per day, paid and unpaid" />
    </div>
    <div class="chart-card">
      <div class="chart-head">
        <h3>Coins per day</h3>
        <p>Coins sent to buyers and coins received, by the day the contract finished.</p>
      </div>
      <app-line-chart [dates]="ch.coins.dates" [series]="ch.coins.series" ariaLabel="Coins sent and received per day" />
    </div>
    <div class="chart-card">
      <div class="chart-head">
        <h3>Collected</h3>
        <p>Of {{ ch.billedTotal | number }} billed in the charted days.</p>
      </div>
      <app-share-bar [parts]="ch.collected" [track]="ch.billedTotal" />
    </div>
  </section>
  @if (ch.truncated) { <p class="chart-note">Charts show the last {{ chartDays }} days of the range; the totals and table cover all of it.</p> }
  }

  <p class="tz" matTooltip="A sell contract counts on the day it finished (Done, Canceled, Expired or Failed). The amount is its fee on the coins its target type bills (sent or received). Contracts that sold nothing are left out.">
    <mat-icon>schedule</mat-icon> Days are in <b>{{ timezone }}</b> · all {{ sell.total | number }} days in range totalled above.
  </p>

  @if (isMobile$ | async) {
  <div class="cards">
    @for (r of sell.rows; track r.date) {
    <div class="card">
      <div class="card-head">
        <div class="card-title">{{ r.date }}</div>
        <span class="card-meta">{{ r.count | number }} contracts</span>
      </div>
      <div class="card-stats">
        <div class="stat"><span class="label">Amount</span><span class="value">{{ r.amount | number }}</span></div>
        <div class="stat"><span class="label">Paid</span><span class="value paid-amount">{{ r.paid | number }}</span></div>
        <div class="stat"><span class="label">Unpaid</span><span class="value unpaid-amount">{{ r.unpaid | number }}</span></div>
      </div>
      <p class="card-meta">Sent {{ r.sent_coins | number }} · received {{ r.received_coins | number }} coins</p>
    </div>
    }
  </div>
  } @else {
  <div class="table-card table-scroll">
    <table class="report-table">
      <thead>
        <tr>
          <th>Day</th><th class="num">Contracts</th><th class="num">Coins sent</th><th class="num">Coins received</th>
          <th class="num">Amount</th><th class="num">Paid</th><th class="num">Unpaid</th>
        </tr>
      </thead>
      <tbody>
        @for (r of sell.rows; track r.date) {
        <tr>
          <td>{{ r.date }}</td>
          <td class="num">{{ r.count | number }}</td>
          <td class="num">{{ r.sent_coins | number }}</td>
          <td class="num">{{ r.received_coins | number }}</td>
          <td class="num strong">{{ r.amount | number }}</td>
          <td class="paid-amount">{{ r.paid | number }}</td>
          <td class="unpaid-amount">{{ r.unpaid | number }}</td>
        </tr>
        }
      </tbody>
      <tfoot>
        <tr>
          <td>All {{ sell.total | number }} days</td>
          <td class="num">{{ sell.totals.count | number }}</td>
          <td class="num">{{ sell.totals.sent_coins | number }}</td>
          <td class="num">{{ sell.totals.received_coins | number }}</td>
          <td class="num strong">{{ sell.totals.amount | number }}</td>
          <td class="paid-amount">{{ sell.totals.paid | number }}</td>
          <td class="unpaid-amount">{{ sell.totals.unpaid | number }}</td>
        </tr>
      </tfoot>
    </table>
  </div>
  }
  }
  } @else {

  @if (!suppliers || (suppliers.rows.length === 0 && suppliers.funnel.registered === 0)) {
  <div class="table-state">No supplier signups or activity in this range.</div>
  } @else {
  <!-- Range totals: supplier counts are distinct over the whole range, not a sum of days. -->
  <div class="tiles">
    <div class="tile tile-total">
      <div class="tile-head">
        <mat-icon>person_add</mat-icon> New suppliers
        <span class="tile-meta">{{ pct(suppliers.funnel.activated, suppliers.funnel.registered) }} activated</span>
      </div>
      <div class="tile-amount num">{{ suppliers.totals.registered | number }}</div>
      <div class="tile-split">
        <span>Activated in range <b class="num">{{ suppliers.totals.activated | number }}</b></span>
      </div>
    </div>
    <div class="tile" matTooltip="Suppliers who added a stock or opened a manual buy contract in the range">
      <div class="tile-head">
        <mat-icon>groups</mat-icon> Active suppliers
      </div>
      <div class="tile-amount num">{{ suppliers.totals.active_suppliers | number }}</div>
      <div class="tile-split">
        <span>New <b class="num">{{ suppliers.totals.activated | number }}</b></span>
        <span>Returning <b class="num">{{ suppliers.totals.active_suppliers - suppliers.totals.activated | number }}</b></span>
      </div>
    </div>
    <div class="tile">
      <div class="tile-head">
        <mat-icon>inventory_2</mat-icon> New stocks
        <span class="tile-meta">{{ suppliers.totals.stock_suppliers | number }} suppliers</span>
      </div>
      <div class="tile-amount num">{{ suppliers.totals.stocks | number }}</div>
    </div>
    <div class="tile">
      <div class="tile-head">
        <mat-icon>description</mat-icon> New buy contracts
        <span class="tile-meta">{{ suppliers.totals.contract_suppliers | number }} suppliers</span>
      </div>
      <div class="tile-amount num">{{ suppliers.totals.contracts | number }}</div>
    </div>
    <div class="tile">
      <div class="tile-head">
        <mat-icon>swap_horiz</mat-icon> Delivering suppliers
        <span class="tile-meta">{{ suppliers.totals.runs | number }} runs</span>
      </div>
      <div class="tile-amount num">{{ suppliers.totals.delivering_suppliers | number }}</div>
      <div class="tile-split">
        <span>Coins <b class="num">{{ suppliers.totals.coins_delivered | number }}</b></span>
      </div>
    </div>
  </div>

  @if (supplierCharts; as ch) {
  <section class="charts" [class.stale]="chartsLoading">
    <div class="chart-card wide">
      <div class="chart-head">
        <h3>Signups and activations per day</h3>
        <p>Suppliers who registered, and suppliers who added their first stock or buy contract.</p>
      </div>
      <app-line-chart [dates]="ch.signups.dates" [series]="ch.signups.series" ariaLabel="Registered and activated suppliers per day" />
    </div>
    <div class="chart-card">
      <div class="chart-head">
        <h3>Suppliers adding work</h3>
        <p>How many suppliers added a stock, and how many opened a manual buy contract, each day.</p>
      </div>
      <app-line-chart [dates]="ch.work.dates" [series]="ch.work.series" ariaLabel="Suppliers adding stocks and buy contracts per day" />
    </div>
    <div class="chart-card">
      <div class="chart-head">
        <h3>Suppliers delivering</h3>
        <p>Suppliers with a transfer run confirmed each day.</p>
      </div>
      <app-line-chart [dates]="ch.delivering.dates" [series]="ch.delivering.series" [area]="true" ariaLabel="Delivering suppliers per day" />
    </div>
  </section>
  @if (ch.truncated) { <p class="chart-note">Charts show the last {{ chartDays }} days of the range; the totals, funnel and tables cover all of it.</p> }
  }

  @if (suppliers.funnel.registered > 0) {
  <section class="charts">
    <div class="chart-card">
      <div class="chart-head">
        <h3>Signup funnel</h3>
        <p>
          What became of the {{ suppliers.funnel.registered | number }} suppliers who registered in this range, up to today.
          @if (suppliers.funnel.median_days_to_first !== null) {
          Half of those who activated did so within <b>{{ days(suppliers.funnel.median_days_to_first) }}</b>.
          }
        </p>
      </div>
      @for (s of funnelSteps(suppliers.funnel); track s.label; let first = $first) {
      <div class="funnel-step" [class.first]="first" [matTooltip]="s.hint">
        <app-share-bar [parts]="s.parts" [track]="suppliers.funnel.registered" />
      </div>
      }
    </div>
    <div class="chart-card">
      <div class="chart-head">
        <h3>Where they come from</h3>
        <p>The "how did you hear about us" answer at registration, for this range's signups.</p>
      </div>
      <div class="table-scroll">
        <table class="report-table compact">
          <thead>
            <tr>
              <th>Source</th><th class="num">Registered</th><th class="num">Activated</th>
              <th class="num">Delivered</th><th class="num">Paid</th>
              <th class="num" matTooltip="Median days from registering to the first stock or buy contract">Days to start</th>
            </tr>
          </thead>
          <tbody>
            @for (s of suppliers.sources; track s.source) {
            <tr>
              <td [class.muted]="!s.source">{{ sourceLabel(s.source) }}</td>
              <td class="num strong">{{ s.registered | number }}</td>
              <td class="num">{{ s.activated | number }} <span class="muted">{{ pct(s.activated, s.registered) }}</span></td>
              <td class="num">{{ s.delivered | number }} <span class="muted">{{ pct(s.delivered, s.registered) }}</span></td>
              <td class="num">{{ s.paid | number }} <span class="muted">{{ pct(s.paid, s.registered) }}</span></td>
              <td class="num">{{ s.median_days_to_first === null ? '–' : days(s.median_days_to_first) }}</td>
            </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </section>
  }

  <p class="tz" matTooltip="Registered counts self-registered suppliers (legacy auto-created accounts are left out). Activated: the supplier's first ever stock or manual buy contract. Buy contracts are manual ones; delivering is a confirmed transfer run. The funnel counts each step up to today, so a recent range has had less time to convert.">
    <mat-icon>schedule</mat-icon> Days are in <b>{{ timezone }}</b> · all {{ suppliers.total | number }} days in range totalled above.
  </p>

  @if (suppliers.rows.length > 0) {
  @if (isMobile$ | async) {
  <div class="cards">
    @for (r of suppliers.rows; track r.date) {
    <div class="card">
      <div class="card-head">
        <div class="card-title">{{ r.date }}</div>
        <span class="card-meta">{{ r.registered | number }} new · {{ r.activated | number }} activated</span>
      </div>
      <div class="card-stats">
        <div class="stat"><span class="label">Active</span><span class="value">{{ r.active_suppliers | number }}</span></div>
        <div class="stat"><span class="label">Stocks</span><span class="value">{{ r.stocks | number }}</span></div>
        <div class="stat"><span class="label">Contracts</span><span class="value">{{ r.contracts | number }}</span></div>
      </div>
      <p class="card-meta">{{ r.delivering_suppliers | number }} delivering · {{ r.coins_delivered | number }} coins</p>
    </div>
    }
  </div>
  } @else {
  <div class="table-card table-scroll">
    <table class="report-table">
      <thead>
        <tr>
          <th rowspan="2">Day</th>
          <th colspan="2" class="grp">Signups</th>
          <th rowspan="2" class="num grp-start">Active</th>
          <th colspan="2" class="grp">Stocks</th>
          <th colspan="2" class="grp">Buy contracts</th>
          <th colspan="3" class="grp">Delivering</th>
        </tr>
        <tr>
          <th class="num">Registered</th><th class="num">Activated</th>
          <th class="num grp-start">Suppliers</th><th class="num">Stocks</th>
          <th class="num grp-start">Suppliers</th><th class="num">Contracts</th>
          <th class="num grp-start">Suppliers</th><th class="num">Runs</th><th class="num">Coins</th>
        </tr>
      </thead>
      <tbody>
        @for (r of suppliers.rows; track r.date) {
        <tr>
          <td>{{ r.date }}</td>
          <td class="num strong">{{ r.registered | number }}</td>
          <td class="num">{{ r.activated | number }}</td>
          <td class="num grp-start">{{ r.active_suppliers | number }}</td>
          <td class="num grp-start">{{ r.stock_suppliers | number }}</td>
          <td class="num">{{ r.stocks | number }}</td>
          <td class="num grp-start">{{ r.contract_suppliers | number }}</td>
          <td class="num">{{ r.contracts | number }}</td>
          <td class="num grp-start">{{ r.delivering_suppliers | number }}</td>
          <td class="num">{{ r.runs | number }}</td>
          <td class="num">{{ r.coins_delivered | number }}</td>
        </tr>
        }
      </tbody>
      <tfoot>
        <tr matTooltip="Supplier counts are distinct over the range, so they are less than the sum of the days when a supplier was active on several.">
          <td>All {{ suppliers.total | number }} days</td>
          <td class="num strong">{{ suppliers.totals.registered | number }}</td>
          <td class="num">{{ suppliers.totals.activated | number }}</td>
          <td class="num grp-start">{{ suppliers.totals.active_suppliers | number }}</td>
          <td class="num grp-start">{{ suppliers.totals.stock_suppliers | number }}</td>
          <td class="num">{{ suppliers.totals.stocks | number }}</td>
          <td class="num grp-start">{{ suppliers.totals.contract_suppliers | number }}</td>
          <td class="num">{{ suppliers.totals.contracts | number }}</td>
          <td class="num grp-start">{{ suppliers.totals.delivering_suppliers | number }}</td>
          <td class="num">{{ suppliers.totals.runs | number }}</td>
          <td class="num">{{ suppliers.totals.coins_delivered | number }}</td>
        </tr>
      </tfoot>
    </table>
  </div>
  }
  }
  }
  }

  @if (!loading && !error && total > 0) {
  <mat-paginator
    [length]="total"
    [pageIndex]="pageIndex"
    [pageSize]="pageSize"
    [pageSizeOptions]="pageSizeOptions"
    [showFirstLastButtons]="true"
    (page)="onPage($event)">
  </mat-paginator>
  }
  </mat-tab-nav-panel>
</div>
